import { type UseMutationResult } from '@tanstack/react-query';

import { parseOrThrow } from '../../../lib/api/contract.js';
import { useApiMutation } from '../../../lib/api/hooks.js';
import {
  createdPaymentGateway,
  PAYMENT_PROVIDER,
  type CreatedPaymentGateway,
  type PaymentGatewayFormInput,
  type PaymentProvider,
} from './schemas.js';
import { paymentGateways, paymentGatewayEndpoints } from './endpoints.js';

/**
 * Writes for the Payment Gateways module.
 *
 * Credentials are sent as a provider-specific object the service encrypts. The
 * form never receives them back, so an edit with blank credential fields keeps
 * the stored set.
 */

const WRITTEN_KEYS = paymentGateways.writtenKeys;

const CREDENTIAL_FIELDS: Record<PaymentProvider, readonly (keyof PaymentGatewayFormInput)[]> = {
  [PAYMENT_PROVIDER.WINDCAVE]: ['windcaveBaseUrl', 'windcaveUsername', 'windcaveAuthKey'],
  [PAYMENT_PROVIDER.STRIPE]: ['stripePublishableKey', 'stripeSecretKey'],
  [PAYMENT_PROVIDER.WESTPAC]: ['westpacMerchantId', 'westpacApiKey', 'westpacPassword'],
  [PAYMENT_PROVIDER.MANUAL]: [],
};

function hasAny(
  values: PaymentGatewayFormInput,
  fields: readonly (keyof PaymentGatewayFormInput)[],
): boolean {
  for (const field of fields) {
    if (typeof values[field] === 'string' && values[field] !== '') return true;
  }
  return false;
}

function credentialsOf(
  values: PaymentGatewayFormInput,
  provider: PaymentProvider,
): Record<string, unknown> | undefined {
  const fields = CREDENTIAL_FIELDS[provider];
  if (fields.length === 0 || !hasAny(values, fields)) return undefined;

  const credentials: Record<string, unknown> = {};
  for (const field of fields) {
    credentials[field] = values[field];
  }
  return credentials;
}

export function createPaymentGatewayPayload(
  values: PaymentGatewayFormInput,
): Record<string, unknown> {
  const provider = values.provider as PaymentProvider;
  const credentials = credentialsOf(values, provider);

  return {
    provider: values.provider,
    name: values.name,
    isActive: values.isActive,
    isDefault: values.isDefault,
    credentials: credentials ?? {},
  };
}

export function updatePaymentGatewayPayload(
  values: PaymentGatewayFormInput,
  version: number,
): Record<string, unknown> {
  const provider = values.provider as PaymentProvider;
  const credentials = credentialsOf(values, provider);

  const payload: Record<string, unknown> = {
    provider: values.provider,
    name: values.name,
    isActive: values.isActive,
    isDefault: values.isDefault,
    version,
  };

  if (credentials !== undefined) payload.credentials = credentials;

  return payload;
}

export function useCreatePaymentGateway(): UseMutationResult<
  CreatedPaymentGateway,
  unknown,
  PaymentGatewayFormInput
> {
  return useApiMutation<PaymentGatewayFormInput, CreatedPaymentGateway>({
    mutationFn: async (api, input) => {
      const response = await api.post<unknown>(
        paymentGatewayEndpoints.create,
        createPaymentGatewayPayload(input),
        crypto.randomUUID(),
      );
      return parseOrThrow(createdPaymentGateway, response, paymentGatewayEndpoints.create);
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdatePaymentGatewayVariables {
  readonly gatewayId: string;
  readonly version: number;
  readonly values: PaymentGatewayFormInput;
}

export function useUpdatePaymentGateway(): UseMutationResult<
  unknown,
  unknown,
  UpdatePaymentGatewayVariables
> {
  return useApiMutation<UpdatePaymentGatewayVariables, unknown>({
    mutationFn: async (api, { gatewayId, version, values }) => {
      await api.patch<unknown>(
        paymentGatewayEndpoints.update(gatewayId),
        updatePaymentGatewayPayload(values, version),
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}

export function useSoftDeletePaymentGateway(): UseMutationResult<unknown, unknown, string> {
  return useApiMutation<string, unknown>({
    mutationFn: async (api, gatewayId) => {
      await api.delete<unknown>(paymentGatewayEndpoints.delete(gatewayId));
    },
    invalidate: WRITTEN_KEYS,
  });
}
